import { memo, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { useNavigate } from 'react-router-dom'
import { postLikeProducts } from '../../api/product'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { useMyContext } from '../../components/provider/MyProvider'
import { AFWebLinkType, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { formatNumberWithTruncation, getParsedSessionStorageItem, onErrorImg, plusComma } from '../../js/script'
import { imgURL } from '../../pages/Home'
import { BtnCart } from '../product/ProductItem'
import { IconStarOn } from '../svg/IconSvg'
import { addToWishlist } from '../../js/pixelEvent'

const RankingCategoryItem = ({
  el,
  idx,
  isAppView,
  setSelectProductNo,
  setIsModal,
  listId,
  listName,
  selectCategory,
}: any) => {
  const [wish, setWish] = useState(false)
  const [categoryNo, setCategoryNo] = useState<any>([])
  const [itemCategory, setItemCategory] = useState<any>()
  const [disCountAcc, setDisCountAcc] = useState(0)
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { setIsLogin, hasAccessToken } = useCommonContext()
  const navigate = useNavigate()

  const cartHandler = useCallback(() => {
    setIsModal(true)
    setSelectProductNo(el.productNo)
  }, [el])

  // GTM Items
  const createGtmItem = useCallback(
    (el: any, idx: number, selectCategory: any, itemCategory: any, listId: any, listName: any) => ({
      item_id: String(el?.productNo ?? ''),
      item_name: el?.productName ?? '',
      affiliation: '어바웃피싱',
      item_list_price: el?.salePrice ?? 0,
      item_selling_price: (el?.salePrice ?? 0) - (el?.immediateDiscountAmt ?? 0),
      price: (el?.salePrice ?? 0) - (el?.immediateDiscountAmt ?? 0),
      item_total_discount: el?.immediateDiscountAmt ?? 0,
      discount: 0,
      index: idx,
      item_brand: el?.brandName ?? '',
      item_category: '낚시용품',
      item_category2: selectCategory?.label ?? '',
      item_category3: itemCategory[0]?.label ?? '',
      item_list_id: String(listId ?? ''),
      item_list_name: listName ?? '',
    }),
    []
  )

  const wishListResponseHandler = (res: any) => {
    if (res.data.length !== 0) {
      const isLiked = res.data[0].like === 'Y'
      setIsToast(true)
      setToastMessage(isLiked ? '찜 리스트에 저장되었습니다.' : '찜 리스트에 제외되었습니다.')
      setToastBtnMessage('바로가기')
      setToastEvent(() => () => {
        if (isAppView) {
          RNPostMessage(AFWebLinkType.moveToLink, {
            url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/wishList`,
          })
        } else {
          navigate('/wishList')
        }
      })

      const gtmWishItem: DataLayerArgs = {
        dataLayer: {
          event: 'add_to_wishlist',
          ecommerce: {
            value: el?.salePrice - el?.immediateDiscountAmt,
            items: [createGtmItem(el, idx, selectCategory, itemCategory, listId, listName)],
          },
        },
      }

      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'add_to_wishlist',
          eventType: {
            value: el?.salePrice - el?.immediateDiscountAmt,
            items: [createGtmItem(el, idx, selectCategory, itemCategory, listId, listName)],
          },
        })
      } else {
        TagManager.dataLayer(gtmWishItem)
      }

      setTimeout(() => {
        setWish((prev) => !prev)
      }, 200)
    }
  }

  const wishClickHandler = () => {
    if (!hasAccessToken) {
      setIsToast(true)
      setToastMessage('로그인이 필요한 서비스입니다.')
      setToastBtnMessage('로그인')
      setToastEvent(() => () => setIsLogin(true))
      const app = getParsedSessionStorageItem('app')
      if (app) {
        RNPostMessage(AFWebLinkType.onSignInInducement)
      }
      return
    }
    const likeItem = {
      like: wish ? 'N' : 'Y',
      productNo: el.productNo,
    }
    postLikeProducts(likeItem)
      .then(wishListResponseHandler)
      .catch((err) => console.log(err))
  }

  const sendEvent = (eventName: string, eventType: any, gtmItem: DataLayerArgs) => {
    if (isAppView) {
      sendSetAppsflyerEvent({
        eventName,
        eventType,
      })
    } else {
      TagManager.dataLayer(gtmItem)
    }
  }

  const productClickHandler = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    e.preventDefault()
    const gtmClickItem = {
      dataLayer: {
        event: 'select_item',
        ecommerce: {
          items: [createGtmItem(el, idx, selectCategory, itemCategory, listId, listName)],
        },
      },
    }

    const gtmItems = gtmClickItem.dataLayer?.ecommerce?.items?.map((item) => item) || []

    const eventType = {
      items: [createGtmItem(el, idx, selectCategory, itemCategory, listId, listName)],
    }

    sendEvent('select_item', eventType, gtmClickItem)
    // setPrevScroll(document.documentElement.scrollTop);
    if (isAppView) {
      RNPostMessage(AFWebLinkType.moveToLink, {
        url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/product/${
          el.productNo
        }?items=${JSON.stringify(gtmItems)}`,
      })
    } else {
      navigate(`/product/${el.productNo}`, {
        state: { items: gtmItems },
      })
    }
  }

  const discountPercentage = useMemo(() => Math.floor((el?.immediateDiscountAmt / el?.salePrice) * 100), [el])
  const salePrice = useMemo(() => plusComma(el?.salePrice - el?.immediateDiscountAmt), [el])
  const originPrice = useMemo(() => plusComma(el?.salePrice), [el])
  const maximumBenefitPrice = useMemo(
    () =>
      plusComma(
        el?.salePrice -
          el?.immediateDiscountAmt -
          el?.couponDiscountAmt -
          (el?.accumulationInfo.rewardRateOfProduct !== 0 ? el?.accumulationInfo?.amount : 0) -
          (el?.accumulationUseInfo?.usable ? Number(disCountAcc) : 0) <
          0
          ? 0
          : el?.salePrice -
              el?.immediateDiscountAmt -
              el?.couponDiscountAmt -
              (el?.accumulationInfo.rewardRateOfProduct !== 0 ? el?.accumulationInfo?.amount : 0) -
              (el?.accumulationUseInfo?.usable ? Number(disCountAcc) : 0)
      ),
    [el, disCountAcc]
  )
  const userMaximumBenefit = useMemo(() => {
    const totalDiscount =
      el?.immediateDiscountAmt +
      el?.couponDiscountAmt +
      (el?.accumulationInfo.rewardRateOfProduct !== 0 ? el?.accumulationInfo?.amount : 0) +
      (el?.accumulationUseInfo?.usable ? Number(disCountAcc) : 0)
    const finalPrice = el?.salePrice - totalDiscount
    return el?.accumulationUseInfo?.usable || el.enableCoupons
      ? finalPrice !== el?.salePrice - el?.immediateDiscountAmt
      : false
  }, [el, disCountAcc])

  useEffect(() => {
    if (el) {
      const count = formatNumberWithTruncation((el?.salePrice - el?.immediateDiscountAmt) * 0.1)
      setDisCountAcc(Number(count.replaceAll(',', '')))
      setCategoryNo(el.displayCategoryNos.split('|'))
    }
    setWish(el?.liked)
  }, [el])
  useEffect(() => {
    if (selectCategory && categoryNo) {
      const results = selectCategory.children.filter((child: any) => categoryNo.includes(String(child.categoryNo)))
      setItemCategory(results)
    }
  }, [selectCategory, categoryNo])

  const paybackInfo = useMemo(() => {
    if (hasAccessToken && el?.accumulationInfo?.amount > 0) {
      return (
        <div className='save_mileage'>
          <span>
            {`페이백 적립금 ${el?.accumulationInfo.rewardRateOfProduct}% `}
            {plusComma(el?.accumulationInfo?.amount)}
            <img src={`${imgURL}/icon_point.png`} alt='' />
          </span>
        </div>
      )
    }
    return null
  }, [hasAccessToken, el])

  return (
    <li key={el.productNo}>
      <div className='item_wrap'>
        {/* 상품 이미지 */}
        <div className='item_img'>
          {/* 품절 */}
          {el?.isSoldOut && (
            <div className='soldout'>
              <img src={`${imgURL}/img_soldout.png`} alt='' />
            </div>
          )}
          {/* 찜하기 */}
          <button
            onClick={() => {
              wishClickHandler()

              addToWishlist(el.productNo, el.productName, el.salePrice, el.immediateDiscountAmt)
            }}
          >
            <img
              src={`${wish ? `${imgURL}/icon_heart_fill.png` : `${imgURL}/icon_heart_outline.png`}`}
              alt='찜하기 아이콘'
            />
          </button>
          {/* <LikeBtn
            item={el}
            wish={wish}
            setWish={setWish}
            setSubmitWish={setLike}
            one={{ label: '낚시용품', categoryNo: '000' }}
            twodepCategory={selectCategory?.label}
            thirdDepLabel={itemCategory?.[0]?.label}
            thirdCategory={itemCategory?.[0]?.categoryNo}
          ></LikeBtn> */}
          {/* 랭킹 플래그, 숫자 */}
          <div className='ranking_flag'>
            <img src={`${imgURL}/icon_flag_rank${idx + 1}.svg`} alt='' />
          </div>
          <div onClick={productClickHandler}>
            {/* dim */}
            <div className='dim'></div>
            {/* 이미지 */}
            <img src={el?.imageUrls[0]} alt='' onError={onErrorImg} />
          </div>
        </div>
        {/* 장바구니 버튼 */}
        {el?.isSoldOut ? (
          <BtnCart
            style={{
              width: '100%',
            }}
            onClick={cartHandler}
          >
            {/* <img src={`${imgURL}/icon_cart.png`} alt="" /> */}
            재입고 알림
          </BtnCart>
        ) : (
          <BtnCart
            style={{
              width: '100%',
            }}
            onClick={cartHandler}
          >
            <img src={`${imgURL}/icon_cart.png`} alt='' /> 장바구니
          </BtnCart>
        )}
        <div style={{ width: '100%' }} onClick={productClickHandler}>
          {/* 상품 브랜드 및 상품명 */}
          {/* 상품의 브랜드명이 없을 때 나오지 않게 함 */}
          {el?.brandName && <div className='item_brand_title'>{el?.brandName}</div>}
          <div className='item_title'>{el?.productName}</div>
          {/* 상품 원 가격 */}
          {discountPercentage === 0 ? (
            <div className='item_sale_wrap'>
              <p className='price'>{salePrice}원</p>
            </div>
          ) : (
            <>
              <div className='item_origin_price'>{originPrice}원</div>
              {/* 상품 할인 퍼센트, 가격 */}
              <div className='item_sale_wrap'>
                <p className='percent'>{discountPercentage}%</p>
                <p className='price'>{salePrice}원</p>
              </div>
            </>
          )}
          {/* 상품 최대 혜택가 */}
          {userMaximumBenefit && (
            <div className='item_maximum_benefit'>
              <p className='badge'>회원혜택가</p>
              <p>{maximumBenefitPrice}원</p>
            </div>
          )}
          {/* 페이백 적립금 5% + 추가적립금 */}
          {paybackInfo}
          {/* 별점 */}
          <div className='item_star_rating'>
            <IconStarOn width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
            <span className='review_rating'>{el?.reviewRating}</span>
            <span className='review_num'>({el?.totalReviewCount})</span>
            {/* Npay */}
            <div className='npay'>
              <img src={`${imgURL}/icon_npay.png`} alt='Naver Pay' />
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}
export default memo(RankingCategoryItem)
